import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, CheckCircle } from 'lucide-react';
interface EventData {
  title: string;
  date: string;
  description: string;
  type: string;
  status: string;
}
const FALLBACK_EVENTS: EventData[] = [{
  title: 'Jarní školení',
  date: '28. února 2026',
  description: 'Každoroční setkání pilotů a lidí napojených na dění kolem letiště Hořovice.',
  type: 'Vzdělávání',
  status: 'Úspěšně proběhlo'
}, {
  title: 'Seskoky padákem',
  date: '23. června 2026',
  description: '0900 až 1700 CET Seskoky padákem včetně tandemových seskoků. Možnost objednání zážitkových poukazů.',
  type: 'Parašutismus',
  status: 'Připravujeme'
}, {
  title: 'Ukázka letadel pro nemocné děti',
  date: '1. srpna 2026',
  description: 'Dětský den s ukázkou letecké techniky na letišti Hořovice pro děti z místní nemocnice.',
  type: 'Zábava',
  status: 'Připravujeme'
}];
const SHEET_CSV_URL = 'https://docs.google.com/spreadsheets/d/1P8KUKdgoPkCdaWhkV74e9z-pZVxyMoyZm3vsTQ43BD4/export?format=csv&gid=0';
function parseCSV(csv: string): string[][] {
  const rows: string[][] = [];
  let current = '';
  let inQuotes = false;
  let row: string[] = [];
  for (let i = 0; i < csv.length; i++) {
    const ch = csv[i];
    const next = csv[i + 1];
    if (inQuotes) {
      if (ch === '"' && next === '"') {
        current += '"';
        i++; // skip escaped quote
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(current.trim());
        current = '';
      } else if (ch === '\n' || ch === '\r' && next === '\n') {
        row.push(current.trim());
        current = '';
        rows.push(row);
        row = [];
        if (ch === '\r') i++; // skip \n after \r
      } else {
        current += ch;
      }
    }
  }
  // Last row
  if (current || row.length > 0) {
    row.push(current.trim());
    rows.push(row);
  }
  return rows;
}
function parseEventsFromCSV(csv: string): EventData[] {
  const rows = parseCSV(csv);
  const events: EventData[] = [];
  let currentEvent: Partial<EventData> = {};
  for (const row of rows) {
    const label = (row[0] || '').toLowerCase().trim();
    const value = (row[1] || '').trim();
    // Detect event header rows like "Akce A", "Akce B", "Akce C"
    if (label.startsWith('akce ')) {
      // If we have a previous event, push it
      if (currentEvent.title) {
        events.push({
          title: currentEvent.title || '',
          date: currentEvent.date || '',
          description: currentEvent.description || '',
          type: currentEvent.type || '',
          status: currentEvent.status || 'Připravujeme'
        });
      }
      currentEvent = {};
      continue;
    }
    if (label === 'kategorie') {
      currentEvent.type = value;
    } else if (label === 'status') {
      currentEvent.status = value;
    } else if (label === 'název') {
      currentEvent.title = value;
    } else if (label === 'datum') {
      currentEvent.date = value;
    } else if (label === 'text') {
      currentEvent.description = value;
    }
  }
  // Push last event
  if (currentEvent.title) {
    events.push({
      title: currentEvent.title || '',
      date: currentEvent.date || '',
      description: currentEvent.description || '',
      type: currentEvent.type || '',
      status: currentEvent.status || 'Připravujeme'
    });
  }
  return events;
}
export function Events() {
  const [events, setEvents] = useState<EventData[]>(FALLBACK_EVENTS);
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        console.log('[EVENTS] Fetching from Google Sheets...');
        const response = await fetch(SHEET_CSV_URL);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const csv = await response.text();
        console.log('[EVENTS] CSV data:', csv.substring(0, 300));
        const parsed = parseEventsFromCSV(csv);
        if (parsed.length > 0) {
          console.log('[EVENTS] Parsed events:', parsed);
          setEvents(parsed);
        } else {
          console.warn('[EVENTS] No events parsed, using fallback');
        }
      } catch (err) {
        console.error('[EVENTS] Failed to fetch events:', err);
      }
    };
    fetchEvents();
  }, []);
  return <section id="events" className="py-24 bg-[#0f1d35] relative overflow-hidden" data-id="element-53">
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#0a1628] to-transparent opacity-50 pointer-events-none" data-id="element-54" />

      <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-55">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4" data-id="element-56">
          <div data-id="element-57">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2" data-id="element-58">
              Nadcházející akce
            </h2>
            <p className="text-white/60" data-id="element-59">
              Přijďte na letiště na tyto speciální akce.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-60">
          {events.map((event, index) => {
          const isDone = event.status.toLowerCase().includes('proběhlo') || event.status.toLowerCase().includes('ukončen');
          return <motion.div key={event.title + index} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className={`bg-[#0a1628] border-l-4 border-[#38bdf8] p-6 rounded-r-xl hover:translate-x-2 transition-transform duration-300`} data-id="element-61">
                <div className="flex items-center justify-between mb-4" data-id="element-62">
                  <span className="bg-[#38bdf8]/10 text-[#38bdf8] text-xs font-bold px-2 py-1 rounded uppercase tracking-wider" data-id="element-63">
                    {event.type}
                  </span>
                  <div className={`flex items-center text-sm ${isDone ? 'text-green-400/70' : 'text-white/40'}`} data-id="element-64">
                    {isDone ? <CheckCircle size={14} className="mr-1" data-id="element-65" /> : <Clock size={14} className="mr-1" data-id="element-66" />}
                    <span data-id="element-67">{event.status}</span>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-white mb-2" data-id="element-68">
                  {event.title}
                </h3>
                <div className="flex items-center text-white/70 text-sm mb-4 font-mono" data-id="element-69">
                  <Calendar size={14} className="mr-2 text-[#38bdf8]" data-id="element-70" />
                  {event.date}
                </div>

                <p className="text-white/50 text-sm leading-relaxed" data-id="element-71">
                  {event.description}
                </p>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}